import { activeModifiers } from '../mods';
import { computeStats, MAX_LEVEL, xpForLevel } from '../stats';
import type { World } from '../World';

/**
 * Recalcule les statistiques effectives du joueur.
 *
 * ⚠️ À appeler DÈS QUE `owned` ou `baseMods` change — c'est-à-dire à la prise
 * d'une carte et à la remise à zéro d'une run.
 *
 * Sans cet appel, tout le système d'améliorations existe sans exister : les
 * cartes s'accumulent, s'affichent, alimentent les compteurs de tags, et le
 * joueur ne sent strictement rien. C'est exactement ce qui s'est produit — le
 * banc d'équilibrage éprouvait `computeStats` en isolation, donc il restait
 * vert pendant que le jeu lisait des constantes en dur.
 *
 * On met en cache plutôt que de recalculer par tick : la composition parcourt
 * tous les modificateurs possédés, et la boucle chaude n'a aucune raison de
 * refaire ce travail soixante fois par seconde pour un résultat identique.
 */
export function syncStats(w: World): void {
  const p = w.progress;
  p.stats = computeStats([...p.baseMods, ...activeModifiers(p.owned)]);
}

/* ═══════════════════════════════════════════════════════════════════════════
   L'expérience et les montées de niveau.

   Une seule subtilité, mais elle compte : la montée de niveau lève un DRAPEAU,
   elle n'ouvre pas l'écran de cartes. La simulation n'a pas à connaître
   l'existence d'un écran — c'est la couche application qui observe le drapeau,
   gèle le jeu et propose les cartes.

   Sans cette séparation, la simulation dépendrait de l'interface, et une partie
   ne pourrait plus être rejouée headless : le replay resterait bloqué à la
   première montée de niveau, en attente d'un clic qui ne viendrait jamais.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Fraction du score convertie en expérience. Un seul facteur, pour que le
 *  rythme des montées se règle d'un seul chiffre.
 *
 *  ⚠️ CALIBRÉ SUR MESURE, pas choisi. La première valeur (0,5) était une
 *  intuition, et elle était treize fois trop généreuse : le niveau 13 tombait
 *  à la fin du seul monde 1, et le niveau 17 en cinq minutes de jeu réel.
 *
 *  Ce n'est pas un simple défaut de confort. Toutes les cartes étant
 *  distribuées avant même le boss, la progression S'ARRÊTAIT au tiers de la
 *  run — les deux mondes suivants n'auraient plus rien eu à offrir, et la
 *  courbe de puissance du plan (§6.7) se serait effondrée.
 *
 *  Le rythme visé est le niveau 7 à la fin du monde 1, soit une carte toutes
 *  les 30 à 40 s. `tests/balance/pacing.test.ts` déroule une vraie partie et
 *  vérifie cette valeur — c'est lui qui l'a produite. */
const XP_PER_SCORE = 0.13;

export function grantXp(w: World, score: number): void {
  const p = w.progress;
  if (p.level >= MAX_LEVEL) return;
  p.xp += score * XP_PER_SCORE;
}

export function progressionSystem(w: World): void {
  const p = w.progress;
  if (p.level >= MAX_LEVEL) return;

  const need = xpForLevel(p.level);
  if (p.xp < need) return;

  p.xp -= need;
  p.level++;
  // ⚠️ Un drapeau, pas un écran. La couche application le consomme.
  p.pendingLevelUps++;
}

/**
 * Sanction de mort : le vaisseau PERD LA MOITIÉ de ses améliorations.
 *
 * ⚠️ Retour de jeu : « même au dernier monde je me retrouve à attendre ». Une
 * fois maxé, plus rien ne menaçait le joueur — la mort ne coûtait qu'une vie
 * sur trois, sans jamais entamer sa puissance. Elle entame désormais son BUILD :
 * on retire la moitié des exemplaires possédés, en commençant par les PLUS
 * RÉCENTS (les derniers acquis, donc les plus chèrement gagnés), et le niveau
 * redescend d'autant. Chaque mort redevient un vrai revers, ce qui rétablit la
 * tension que la puissance de fin de partie avait effacée.
 *
 * On retire par la fin plutôt qu'au hasard : c'est reproductible (donc
 * déterministe et rejouable), et cela préserve le socle du build — le joueur
 * garde ses premiers choix, ceux qui définissent sa voie, et perd les
 * raffinements. Perdre la fondation au hasard serait vécu comme arbitraire.
 */
export function penalizeDeath(w: World): void {
  const p = w.progress;
  let total = 0;
  for (const o of p.owned) total += o.count;
  let toRemove = Math.floor(total / 2);
  if (toRemove <= 0) {
    // Rien à perdre (aucune ou une seule amélioration) : la mort ne coûte que
    // la vie. On annule tout de même une montée de niveau en attente — la
    // récompense d'un palier qu'on n'a pas eu le temps d'encaisser ne survit
    // pas à la mort qui a suivi.
    p.pendingLevelUps = 0;
    p.xp = 0;
    return;
  }

  // Retrait par la fin : `owned` est dans l'ordre d'acquisition.
  for (let i = p.owned.length - 1; i >= 0 && toRemove > 0; i--) {
    const o = p.owned[i]!;
    const take = o.count < toRemove ? o.count : toRemove;
    o.count -= take;
    toRemove -= take;
    if (o.count <= 0) p.owned.pop();
  }

  // Le niveau suit le nombre d'exemplaires restants : chaque acquisition valait
  // un palier, la perte en rend autant. Plancher à 1.
  let remaining = 0;
  for (const o of p.owned) remaining += o.count;
  p.level = remaining + 1 > 1 ? remaining + 1 : 1;
  p.xp = 0;
  p.pendingLevelUps = 0;

  // ⚠️ Recomposer les statistiques : sans ça, `playerSystem` continuerait de
  // tirer avec le build d'AVANT la mort. C'est exactement le défaut qui rendait
  // les cartes décoratives — la sanction serait invisible.
  syncStats(w);
}

/** Fraction du palier courant déjà parcourue — pour la jauge du HUD. */
export function xpRatio(w: World): number {
  const p = w.progress;
  if (p.level >= MAX_LEVEL) return 1;
  const need = xpForLevel(p.level);
  return need > 0 ? Math.min(1, p.xp / need) : 0;
}
